import type { RecordingMetadataSpeaker } from "@repo/app-types/entities/recording";
import type { CustomerInteractionText } from "@repo/app-types/entities/teammatePerformanceSummary";
import { IconMessage } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { TranscriptContainer } from "../../components/transcript/transcriptContainer.js";
import { TranscriptMessage } from "../../components/transcript/transcriptMessage.js";
import type { QuoteMatchMap } from "./gradeValidationHelpers.js";
import { renderSpeakerName } from "./gradingHelpers.js";
import { RecordingFragmentText } from "./snippetSharing/recordingFragmentText.js";
import type { FocusedSnippetState } from "./snippetSharing/useTranscriptSnippetSharing.js";

export interface GradingTranscriptTextStretchProps {
  /** Oldest first. */
  texts: readonly CustomerInteractionText[];
  speakers: readonly RecordingMetadataSpeaker[];
  activeSnippet?: FocusedSnippetState;
  quoteMatches?: QuoteMatchMap;
  searchMatches?: QuoteMatchMap;
}

/** A run of texts between an interaction's calls. */
export const GradingTranscriptTextStretch: FC<
  GradingTranscriptTextStretchProps
> = (props) => {
  const { texts, speakers, activeSnippet, quoteMatches, searchMatches } = props;

  return (
    <TranscriptContainer>
      <div className="flex items-center space-x-2 py-2 text-sm">
        <IconMessage className="size-4 text-blue-500" />
        <span>Texts</span>
      </div>

      {texts.map((text) => (
        <TranscriptMessage
          key={text.id}
          speakerName={renderSpeakerName(speakers, text.speaker_id)}
          speakerRole={
            speakers.find((speaker) => speaker.id === text.speaker_id)
              ?.role_type ?? undefined
          }
        >
          <p
            className="whitespace-pre-wrap"
            data-recording-id={text.transcript_target?.recording_id}
            data-fragment-index={text.transcript_target?.fragment_index}
            data-timestamp={text.transcript_target?.start_time}
          >
            {text.transcript_target ? (
              <RecordingFragmentText
                text={text.body}
                recordingId={text.transcript_target.recording_id}
                fragmentIndex={text.transcript_target.fragment_index}
                activeSnippet={activeSnippet}
                quoteMatches={quoteMatches}
                searchMatches={searchMatches}
              />
            ) : (
              text.body
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            {DateTime.fromISO(text.at).toLocaleString(DateTime.DATETIME_MED)}
          </p>
        </TranscriptMessage>
      ))}
    </TranscriptContainer>
  );
};
